import { createRoot, type Root } from 'react-dom/client';
import { App, delFramework, getPreviewModeWrapperId, InstanceData, InstanceDataType, StoreProvider } from '@/framework';
import { History } from '@/framework/history';
import { Framework } from '@/framework/instance';
import { Active, CustomLayer, Font, Result, ShortcutMenu, Stage, Tip } from '@/framework/plugins';
import { Layer } from '@/layers/layer';
import {
    BaseClassInstance,
    DEFAULT_STAGE_PADDING,
    IEditorConfig,
    ILayerProps,
    IRegisterPluginOptions,
    PosterType,
} from '@/version-types';
import { loadInnerPlugins } from './load-inner-plugins';

export class Editor extends Framework {
    private container: HTMLElement | null;
    private root: Root | null = null;
    private isLoaded = false;
    id: string;
    /** 提示 */
    tip!: Tip;
    /** 字体注册 */
    font!: Font;
    /** 基础图层 */
    layer!: Layer;
    /** 舞台操作 */
    stage!: Stage;
    /** 激活图层操作 */
    active!: Active;
    /** 获取结果 */
    result!: Result;
    /** 历史记录 */
    history!: History;
    /** 自定义图层 */
    customLayer!: CustomLayer;
    /** 激活图层上方快捷键菜单 */
    shortcutMenu!: ShortcutMenu;

    constructor(id: string, _config?: IEditorConfig) {
        super(id);
        this.id = id;
        this.container = null;
        this.framework.setConfig({ padding: DEFAULT_STAGE_PADDING, ..._config });
        // 加载内置插件
        loadInnerPlugins(this.registerPlugins.bind(this));
        // 挂载内置实例
        this.mountInnerInstance();
    }

    /** 挂载内置实例 */
    private mountInnerInstance() {
        // 基础功能实例
        this.history = this.framework.history;
        this.tip = this.getInstance(Tip);
        this.font = this.getInstance(Font);
        this.layer = this.getInstance(Layer);
        this.stage = this.getInstance(Stage);
        this.active = this.getInstance(Active);
        this.result = this.getInstance(Result);
        this.customLayer = this.getInstance(CustomLayer);
        this.shortcutMenu = this.getInstance(ShortcutMenu);
    }

    getPreviewModeWrapperId = () => getPreviewModeWrapperId(this.id);

    /** 初始化数据 */
    init() {
        // 框架模块初始化
        this.framework.init();
        this.framework.destroy();
        this.framework.stage.destroy();
        this.framework.operate.destroy();
        this.framework.history.destroy();
        // 功能初始化
        this.framework.destroyPlugins();
    }

    /** 获取图层行为 */
    getBehavior(layer: ILayerProps) {
        return this.framework.getBehavior(layer);
    }

    /**
     * 注册插件
     *
     * @param configs - 插件配置
     */
    registerPlugins(configs: IRegisterPluginOptions[]) {
        this.framework.registerPlugins(configs);
    }

    /**
     * 注册实例
     *
     * @param configs - 实例配置
     */
    registerInstance<T extends Record<string, any>>(configs: T): void {
        Object.entries(configs).forEach(([key, instance]) => {
            this[key] = instance;
        });
    }
    /**
     * 根据类名获取实例 - 重载1：传入构造函数，返回该构造函数的实例类型
     *
     * @param classConstructor - 类构造函数
     */
    getInstance<T extends BaseClassInstance>(classConstructor: T): InstanceType<T>;
    /**
     * 根据类名获取实例 - 重载2：传入字符串名称，返回泛型类型
     *
     * @param name - 类名字符串
     */
    getInstance<T = any>(name: string): T;
    /**
     * 根据类名获取实例 - 实现
     */
    getInstance<T extends BaseClassInstance>(classConstructorOrName: T | string): InstanceType<T> | any {
        if (typeof classConstructorOrName === 'string') {
            return this.framework.getInstance<T>(classConstructorOrName);
        }
        return this.framework.getInstance(classConstructorOrName);
    }

    /** 获取配置 */
    getConfig() {
        return this.framework.getConfig();
    }

    /** 设置用户唯一标识 */
    setUid(uid: number) {
        this.framework.setUid(uid);
    }

    /** 取消事件监听 */
    off(handler: number) {
        this.framework.events.off(handler);
    }

    on(event: string, fn: (...args: any[]) => void, weight = 0) {
        return this.framework.events.on(event, fn, weight);
    }

    emit(eventName: string, ...args: any[]) {
        return this.framework.events.emit(eventName, ...args);
    }

    /** 监听快捷键 */
    onHotkey(key: string, fn: (key: string) => void, weight = 0) {
        return this.framework.events.on(key, fn, weight);
    }

    /** 触发快捷键 */
    emitHotkey(key: string, ...args: any[]) {
        return this.framework.events.emit(key, ...args);
    }

    // TODO: 待确认这里是否只替换原来的 LAYER_BASE_EVENT.delete，LAYER_BASE_EVENT.delete 的定义是图层删除，onDelete 目前的实现还只是 Delete 键的侦听触发
    /** 监听键盘删除 */
    onDelete(fn: (type?: string) => void, weight = 0) {
        return this.framework.events.onDelete(fn, weight);
    }

    /** 触发删除 */
    emitDelete(type?: string) {
        return this.framework.events.emitDelete(type);
    }

    /** 监听esc */
    onEsc(fn: () => void, weight = 0) {
        return this.framework.events.onEsc(fn, weight);
    }

    /** 监听回车 */
    onEnter(fn: () => void, weight = 0) {
        return this.framework.events.onEnter(fn, weight);
    }

    /** 监听鼠标按下 */
    onMouseDown(fn: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void, weight = 0) {
        return this.framework.events.onMouseDown(fn, weight);
    }

    /** 获取缩放 */
    getScale() {
        return this.framework.stage.getScale();
    }

    /** 设置缩放 */
    setScale(scale: number) {
        this.framework.stage.setScale(scale);
    }

    /** 设置是否自动缩放 */
    setIsAutoScale(isAutoScale: boolean) {
        this.framework.stage.setIsAutoScale(isAutoScale);
    }

    /** 监听缩放变化 */
    onScale(fn: (scale: number) => void) {
        return this.framework.events.onScale(fn);
    }

    /** 监听基础操作状态变化 */
    onBaseOperateChange(fn: (isBaseOperate: boolean) => void) {
        const handler = this.framework.events.onBaseOperateChange(fn);
        return () => this.framework.events.off(handler);
    }

    /** 设置自动缩放 */
    setAutoScale(autoScale: boolean) {
        this.framework.stage.setIsAutoScale(autoScale);
    }

    /** 放大 */
    zoomIn() {
        this.framework.stage.zoomIn();
    }

    /** 缩小 */
    zoomOut() {
        this.framework.stage.zoomOut();
    }

    /** 设置主题 */
    setTheme(theme: string) {
        this.framework.setTheme(theme);
    }
    /** 获取主题 */
    getTheme() {
        return this.framework.getTheme();
    }

    /** 设置海报类型 */
    setPosterType(posterType: PosterType) {
        this.framework.setPosterType(posterType);
    }

    /** 设置容器尺寸 */
    setViewport(width: number, height: number) {
        this.framework.stage.setViewInfo(width, height);
    }

    /** 禁用全部操作 */
    disableOperate() {
        this.framework.operate.disableOperate();
    }

    /** 启用全部操作 */
    enableOperate() {
        this.framework.operate.enableOperate();
    }

    /** 禁用基础操作 */
    disableBasicOperate(operate: {
        pick?: boolean;
        move?: boolean;
        resize?: boolean;
        rotate?: boolean;
        scale?: boolean;
        active?: boolean;
    }) {
        if (operate.pick) {
            this.disablePick();
        }
        if (operate.move) {
            this.disableMove();
        }
        if (operate.resize) {
            this.disableResize();
        }
        if (operate.rotate) {
            this.disableRotate();
        }
        if (operate.scale) {
            this.disableScale();
        }
        if (operate.active) {
            this.disableActive();
        }
    }

    /** 启用基础操作 */
    enableBasicOperate(operate: {
        pick?: boolean;
        move?: boolean;
        resize?: boolean;
        rotate?: boolean;
        scale?: boolean;
    }) {
        if (operate.pick) {
            this.enablePick();
        }
        if (operate.move) {
            this.enableMove();
        }
        if (operate.resize) {
            this.enableResize();
        }
        if (operate.rotate) {
            this.enableRotate();
        }
        if (operate.scale) {
            this.enableScale();
        }
    }

    /** 禁用选中 */
    disablePick(delActiveLayer = true) {
        this.framework.operate.disablePick(delActiveLayer);
    }

    /** 启用选中 */
    enablePick() {
        this.framework.operate.enablePick();
    }

    /** 禁用移动 */
    disableMove() {
        this.framework.operate.disableMove();
    }

    /** 启用移动 */
    enableMove() {
        this.framework.operate.enableMove();
    }

    /** 禁用缩放 */
    disableScale() {
        this.framework.operate.disableScale();
    }

    /** 启用缩放 */
    enableScale() {
        this.framework.operate.enableScale();
    }

    /** 禁用旋转 */
    disableRotate() {
        this.framework.operate.disableRotate();
    }

    /** 启用旋转 */
    enableRotate() {
        this.framework.operate.enableRotate();
    }

    /** 禁用尺寸调整 */
    disableResize() {
        this.framework.operate.disableResize();
    }

    /** 禁用激活 */
    disableActive() {
        this.framework.operate.disableActive();
    }

    /** 启用激活 */
    enableActive() {
        this.framework.operate.enableActive();
    }

    /** 启用尺寸调整 */
    enableResize() {
        this.framework.operate.enableResize();
    }

    /** 禁用鼠标选择 */
    disableMouseSelect() {
        this.framework.operate.disableMouseSelect();
    }

    /** 启用鼠标选择 */
    enableMouseSelect() {
        this.framework.operate.enableMouseSelect();
    }

    /** 是否禁用全部操作 */
    isDisabled() {
        return this.framework.operate.isDisableOperate();
    }

    /** 监听编辑器状态 */
    onState(
        fn: (args: {
            disableOperate: boolean;
            disablePick: boolean;
            disableMouseSelect: boolean;
            disableMove: boolean;
            disableScale: boolean;
            disableRotate: boolean;
            disableResize: boolean;
            isBaseOperate: boolean;
            isMouseSelect: boolean;
        }) => void,
    ) {
        const handle = this.framework.events.onEditorOperateState(fn);
        return () => this.framework.events.off(handle);
    }

    /** 监听图片合成失败 */
    onBehaviorErrorImage(fn: (error: { layerId: string; imageUrl: string }) => void) {
        const handler = this.framework.events.onBehaviorErrorImage(fn);

        return () => {
            this.framework.events.off(handler);
        };
    }

    /** 挂载 */
    mount() {
        if (!this.container) {
            this.container = document.getElementById(this.id)!;
        }
        if (!this.container) {
            throw new Error(`container not found: ${this.id}`);
        }
        if (!this.isLoaded) {
            this.isLoaded = true;
            this.framework.createHistory();
        }
        const state = InstanceData.getData(this.id, InstanceDataType.State) as any;
        if (this.container) {
            this.root = this.root || createRoot(this.container);
            this.root.render(
                <StoreProvider state={state}>
                    <App />
                </StoreProvider>,
            );
        }
    }

    /** 卸载 */
    unmount() {
        if (this.container) {
            this.root?.unmount();
            this.root = null;
        }
    }

    /** 销毁 */
    destroy() {
        delFramework(this.id);
        this.unmount();
        this.container = null;
    }
}
